import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Spinner, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { handleTokenAndPostRequest } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import AgentAuthorRow from '../components/AgentAuthorRow';
import './Styles.css';

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function estimateReadTime(content) {
  if (!content) return 1;
  const text = content.replace(/<[^>]+>/g, ' ');
  const wordCount = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / 250));
}

function NewsListPage() {
  const { agent_slug, agentDetails } = useAgentDetails();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} - News & Insights`;
    }
  }, [agentDetails]);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/get-articles`,
          { agent_slug: agent_slug },
          { 'Content-Type': 'application/json' }
        );
        const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        const sorted = (Array.isArray(data) ? data : []).sort(
          (a, b) => new Date(b.publish_date) - new Date(a.publish_date)
        );
        setArticles(sorted);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError('Unable to load articles right now. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    if (agent_slug) {
      fetchArticles();
    }
  }, [agent_slug]);

  if (!agentDetails) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const agentParam = agentDetails.landing_page_slug !== 'home-price' ? `?agent=${agentDetails.landing_page_slug}` : '';

  return (
    <div style={{ paddingBottom: '100px' }}>
      <Container style={{ maxWidth: 900, marginTop: isMobile ? 80 : 100 }}>
        <AgentAuthorRow agentDetails={agentDetails} showDivider={false} align="center" />

        <h1 style={{ fontWeight: 700, fontSize: isMobile ? '1.6rem' : '2rem', marginBottom: 4, textAlign: 'center' }}>
          News & Insights
        </h1>
        <p style={{ color: '#6c757d', textAlign: 'center', marginBottom: 32, fontSize: isMobile ? '0.95rem' : '1.05rem' }}>
          Stay informed with the latest property market news and expert analysis.
        </p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: '#6c757d' }}>
            <p>{error}</p>
          </div>
        ) : articles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: '#6c757d' }}>
            <p>No articles available yet. Check back soon!</p>
          </div>
        ) : (
          <Row>
            {articles.map((article, index) => (
              <Col xs={12} md={index === 0 ? 12 : 6} key={article.slug} style={{ marginBottom: 24 }}>
                <Link
                  to={`/news/${article.slug}${agentParam}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Card
                    style={{
                      border: 'none',
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                      height: '100%',
                      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    }}
                    className="article-card-hover"
                  >
                    {article.cover_image && (
                      <Card.Img
                        variant="top"
                        src={article.tiny_image || article.cover_image}
                        alt={article.title}
                        referrerPolicy="no-referrer"
                        style={{
                          height: index === 0 ? (isMobile ? 200 : 320) : (isMobile ? 180 : 200),
                          objectFit: 'cover',
                        }}
                      />
                    )}
                    <Card.Body style={{ padding: isMobile ? '16px' : '20px' }}>
                      <Card.Title
                        style={{
                          fontWeight: 700,
                          fontSize: index === 0 ? (isMobile ? '1.15rem' : '1.4rem') : (isMobile ? '1rem' : '1.1rem'),
                          lineHeight: 1.3,
                          marginBottom: 8,
                        }}
                      >
                        {article.title}
                      </Card.Title>
                      {article.description && (
                        <Card.Text
                          style={{
                            color: '#555',
                            fontSize: isMobile ? '0.88rem' : '0.95rem',
                            lineHeight: 1.5,
                            marginBottom: 12,
                            display: '-webkit-box',
                            WebkitLineClamp: index === 0 ? 3 : 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.description}
                        </Card.Text>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', color: '#888', fontSize: '0.82rem' }}>
                        {article.publish_date && (
                          <span>{formatDate(article.publish_date)}</span>
                        )}
                        {article.content && (
                          <>
                            <span style={{ margin: '0 6px' }}>&middot;</span>
                            <span>{estimateReadTime(article.content)} min read</span>
                          </>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        )}
      </Container>

      <style>{`
        .article-card-hover:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important;
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}

export default NewsListPage;
